import { startTransition, useCallback, useMemo, useRef, useState } from 'react'
import { t } from 'i18next'
import { v4 as uuidv4 } from 'uuid'

import { ColumnTypes, ITableRow } from '@makyoapp/makyo-sdk/models/Table'
import {
  ActionFlag,
  BuilderDroppableElement,
  BuilderMenu,
  BuilderPage,
  IActionSettings,
  IActionType,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'
import { isColorVariant, MaterialIcon, sonner } from '@makyoapp/makyo-tw-ui'

import { globalVariableClient } from '@/core/app-builder/global-variables'
import { parseProfileData } from '@/core/app-builder/parser/profile-data'
import { parseSingleCellValue } from '@/core/app-builder/parser/single-cell'
import {
  parseFilterDataAsync,
  parseVariableText,
  parseVariableTextActionOutput,
} from '@/core/app-builder/parser/variable-text'
import { IDB, IDBTable } from '@/core/db'
import { getDefaultColumnValue } from '@/core/db/utils'
import { IRow } from '@/core/models/tables/row'
import { WORKER_TIMEOUT_MS } from '@/workers/worker-request'
import constate from '@/lib/constate'
import cookies from '@/lib/cookies'
import { generateRandomId } from '@/lib/random-id'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useAppRoutingContext } from '@/context/__project__/AppRoutingContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useReqError } from '@/hooks/useReqError'
import {
  ActionDataType,
  ActionHandlerOption,
  ActionHandlers,
  ActionInputdata,
  IActionSequence,
  SimulateActionOptions,
} from '@/shared/action.types'
import {
  addCacheBusterToFirstUrl,
  copyToClipboard,
  downloadFile,
  openGoogleMaps,
  playAudio,
  prepareFormPayload,
  sanitizeWhatsappMessage,
} from '@/shared/action.utils'
import { LeadingSidebarTab } from '@/shared/builder-ui'
import { removeActionFlag } from '@/shared/builder-utils'
import { collectTableIds } from '@/shared/collect-table-ids'
import { STORAGE_KEYS } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'
import { DEFAULT_OVERLAY_SETTINGS } from '@/shared/overlay'
import { formatPhoneNumber } from '@/shared/phone-number'
import { createSlug } from '@/shared/utils'

import { useAppPagesContext } from '../AppPagesContext'
import { useAppClientContext } from './AppClientContext'
import { useDbClientContext } from './DbClientContext'
import { useSimulateActionV2 } from './hooks/useSimulateActionV2'

type ActionSeqType = 'normal' | 'queue'

function filterAvailableRowIds(rowIds: string[], rows: ITableRow[]): string[] {
  const availableIds = new Set(rows.map((row) => row._id))
  return rowIds.filter((id) => availableIds.has(id))
}

function exitActionQueue() {
  return new Promise<void>((resolve) => resolve())
}

/**
 * Builder action context V2
 *
 * @todo
 *  - replace rowIndex reference with rowId
 *
 */
const [BuilderActionProviderV2, useBuilderActionContextV2] = constate(
  'BuilderActionContextV2',
  ({ currentOrgId }: { currentOrgId: string }) => {
    const { appID } = useAppClientContext()
    const { dbClient, dbWorker, language, userSpecific, loadedTables, tables } = useDbClientContext()
    const { activeLayerClient } = useActiveLayerContext()
    const { userId, userEmail, handleLogout } = useAppAuthContext()
    const {
      pages,
      menus,
      dialogs,
      dialogAction,
      setDialogAction,
      setActiveTab,
      setPageAction,
      listChecked,
      setListChecked,
      project,
      appUserTable,
    } = useAppBuilderContext()

    // privacy settings
    const privacySettings = project?.privacy
    const unauthenticatedPageId = privacySettings?._PostUnauthenticatedPageID
    const unauthenticatedPageSlug = unauthenticatedPageId
      ? pages.find((p) => p.id === unauthenticatedPageId)?.slug
      : undefined

    const { goToPage, handleGoBack } = useAppRoutingContext()
    const { updatePageAlt, goToPageSafely } = useAppPagesContext()
    const {
      overlays,
      activeOverlays,
      showOverlay,
      showDetailScreenOverlay,
      changeDetailsOverlayIndex,
      closeOverlay,
      closeAllOverlays,
    } = useAppOverlaysContext()

    const { activeLayer, activeLayerTableId, activeLayerDetailRowId, setActiveComponent } = useActiveLayerContext()

    const printAPIError = useReqError()

    // remove existing snackbar if any
    const handleCloseLastSnackbar = useCallback(() => {
      sonner.dismiss()
    }, [])

    const handleResetCheckedList = useCallback(() => {
      if (listChecked && listChecked.items.length > 0) setListChecked(undefined)
    }, [listChecked, setListChecked])

    //--------------------------------------------------------------------------------------//
    //                            Action Cache & States                                     //
    //--------------------------------------------------------------------------------------//

    const actionSequenceRef = useRef<Map<string, IActionSequence>>(new Map())
    const dialogSequenceRef = useRef<Map<string, string>>(new Map())
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const activeMenuAnchorRef = useRef<HTMLElement | null>(null)
    const [activeMenu, setActiveMenu] = useState<BuilderMenu | null>(null)
    const [menuOpened, setMenuOpened] = useState(false)

    //--------------------------------------------------------------------------------------//
    //                             Value Parser & Formatter                                 //
    //--------------------------------------------------------------------------------------//

    const parseActionSettingsData = useCallback(
      async (settings: Record<string, any>, sequenceId: string) => {
        if (!dbClient) return null

        const actionSequence = actionSequenceRef.current.get(sequenceId)!

        const { triggerSource, external, outputCache } = actionSequence

        // handle new `clear value` data type
        if (settings.dataType === ActionDataType.ClearValue) {
          return '##clear_value##'
        }

        // handle newsletter data type
        if (
          settings.dataType === ActionDataType.NewsletterEmail ||
          settings.dataType === VariableSelectFeatureFlag.NewsletterEmail ||
          settings.dataType === VariableTextFeatureFlag.NewsletterEmail
        ) {
          return external?.newsletter?.email || ''
        }

        // handle unique id data type
        if (settings.dataType === ActionDataType.UniqueID) {
          return generateRandomId()
        }

        // handle datetime data type
        if (settings.dataType === ActionDataType.DateTime) {
          return new Date().toISOString()
        }

        // handle temporary variable data type
        if (settings.dataType === ActionDataType.ActionOutput) {
          const actionId = settings.value
          const actionOutput = external?.allActions?.find((action) => action.id === actionId)
          if (actionOutput) {
            if (actionOutput.type === IActionType.TemporaryVariable) {
              if (!dbClient) return null
              const theValue = await parseVariableTextActionOutput(
                actionOutput.settings.value,
                dbClient,
                activeLayerClient,
                userEmail,
                language,
                userSpecific,
                external?.detailsPage?.detailTableId,
                external?.detailsPage?.detailRowId ?? triggerSource?.rowId,
                external?.allActions || [],
                outputCache,
                external
              )
              return theValue
            }

            if (actionOutput.type === IActionType.FilterData) {
              const chosenColId = settings.colId
              const theResult = await parseFilterDataAsync(
                actionOutput.id,
                external?.allActions || [],
                dbClient,
                activeLayerClient,
                userEmail,
                language,
                userSpecific,
                {
                  detailsPage: {
                    detailTableId:
                      external?.detailsPage?.detailTableId ?? actionOutput.settings.tableId ?? triggerSource?.tableId,
                    detailRowId: external?.detailsPage?.detailRowId ?? triggerSource?.rowId,
                  },
                }
              )

              if (theResult) {
                if (Array.isArray(theResult)) {
                  return theResult.map((row) => row[chosenColId]).join('')
                } else {
                  if (theResult[chosenColId] && typeof theResult[chosenColId].value === 'string') {
                    return theResult[chosenColId].value
                  }
                  return theResult[chosenColId]?.toString()
                }
              }
              return null
            }

            if (actionOutput.type === IActionType.FormAddRow) {
              const data = outputCache.get(actionId)
              if (data) {
                const chosenColId = settings.colId
                return data[chosenColId]?.toString() || ''
              }
              return null
            }
          }
          return null
        }

        // handle profile data type
        if (settings.dataType === ActionDataType.ProfileData) {
          let col = settings.value
          const profileDataValue = await parseProfileData(col, dbClient, userEmail)
          return profileDataValue
        }

        // handle session id data type
        if (settings.dataType === ActionDataType.SessionID) {
          // Check current cookie
          const sessionId = cookies.getAppSessionId(appID)

          if (!sessionId) {
            // create new one
            const newSessionId = crypto.randomUUID()
            cookies.setAppSessionId(appID, newSessionId)
            return newSessionId
          }

          // return the cookie value
          return sessionId
        }

        // handle new comment data type
        if (settings.dataType === ActionDataType.Comment) {
          switch (settings.value) {
            case 'comment':
              return external?.comment?.comment
            case 'parent-id':
              return external?.comment?.parentId
            default:
              return null
          }
        }

        // handle currentRow (details page)
        if (settings.dataType === ActionDataType.CurrentRow) {
          const col = settings.value

          const tableId = external?.detailsPage?.detailTableId
          if (!tableId) return col

          const dbTable = dbClient.tablesMap.get(tableId)
          if (!dbTable) return col

          const rowId = external?.detailsPage?.detailRowId
          if (!rowId) return col

          const rowIdx = dbTable.GetRowIndexByRowId(rowId, language, userSpecific)
          if (typeof rowIdx !== 'number' || rowIdx === -1) return col

          const value = parseSingleCellValue({ col, tbl: tableId, row: rowIdx }, dbClient, language, userSpecific)

          return value ?? col
        }

        // !! DO NOT TOUCH !!
        // parse value
        let tableId = settings.tableId
        if (!tableId) return settings.value

        const dbTable = dbClient.tablesMap.get(tableId)
        if (!dbTable) return settings.value

        let rowId = settings.rowId ?? triggerSource?.rowId ?? external?.detailsPage?.detailRowId
        if (!rowId) return settings.value

        let rowIndex = dbTable.GetRowIndexByRowId(rowId, language, userSpecific)

        return parseSingleCellValue(
          {
            col: settings.value,
            tbl: tableId,
            row: rowIndex,
          },
          dbClient,
          language,
          userSpecific
        )
      },
      [dbClient, activeLayerClient, userEmail, language, userSpecific, appID]
    )

    const parseMultipleRowIdsConfig = useCallback(
      async (dbClient: IDB, { settings, sequenceId }: ActionHandlerOption) => {
        const actionSequence = actionSequenceRef.current.get(sequenceId)!

        const { triggerSource, external, multipleOptions } = actionSequence

        let tableId = undefined
        let rowIds: string[] = []
        let dbTable: IDBTable | undefined

        let isMultiple = !!multipleOptions && !!multipleOptions.tableId && !!multipleOptions.rowIds.length

        try {
          // multiple rows
          if (!!multipleOptions && !!multipleOptions.tableId && !!multipleOptions.rowIds.length) {
            tableId = multipleOptions?.tableId
            const table = dbClient.tablesMap.get(tableId)

            if (table) {
              dbTable = table
              const rows = dbTable.GetDbWorkerRows(language, userSpecific)
              rowIds = filterAvailableRowIds(multipleOptions.rowIds, rows)
            }
          }
          // single row
          else {
            tableId = settings.tableId ?? triggerSource?.tableId ?? external?.detailsPage?.detailTableId
            const table = dbClient.tablesMap.get(tableId)

            if (table) {
              dbTable = table
              if (settings.isCurrentUser) {
                rowIds = userId ? [userId] : []
              } else {
                const cacheRowId = triggerSource?.rowId ?? external?.detailsPage?.detailRowId

                // recommended: use rowId instead of rowIndex
                if (cacheRowId) {
                  rowIds = [cacheRowId]
                } else {
                  const rowId = triggerSource?.rowId ?? external?.detailsPage?.detailRowId
                  if (rowId) {
                    rowIds = [rowId]
                  }
                }
              }
            }
          }
        } catch (err) {
          //
        }

        return { tableId, dbTable, rowIds, isMultiple }
      },
      [language, userSpecific, userId]
    )

    const formatRowDataPayload = useCallback(
      async (data: Record<string, ActionInputdata>, sequenceId: string) => {
        let payload = Object.entries(data).reduce(async (prev, [col, inputData]) => {
          let parsedValue = await parseActionSettingsData(inputData, sequenceId)
          let hasValue = parsedValue !== null && parsedValue !== ''
          return {
            ...(await prev),
            ...(hasValue && { [col]: parsedValue }),
          }
        }, {}) as ITableRow
        return payload
      },
      [parseActionSettingsData]
    )

    //--------------------------------------------------------------------------------------//
    //                                  Data Actions                                        //
    //--------------------------------------------------------------------------------------//
    const handleAddRow = useCallback(
      async ({ settings, sequenceId }: ActionHandlerOption) => {
        if (!dbClient) return null

        // Target table: where the new row will be added
        const targetTableId = settings.tableId
        const table = dbClient?.tablesMap.get(targetTableId)

        const dataToAdd = settings.data as Record<string, ActionInputdata>

        if (table) {
          try {
            // Format the payload using source row context for data parsing
            const payload = await formatRowDataPayload(dataToAdd, sequenceId)

            // Add the new row to the target table
            const newRowId = await table
              .AddRow(payload as ITableRow, language, userSpecific)
              .then((newRow) => newRow?._id)

            if (!newRowId) return null

            // wait for the table to be loaded, then grab the row data
            return new Promise<IRow | null>((resolve) => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
              }

              const cleanup = () => {
                window.removeEventListener(EVENT_KEYS.DB_TABLE_DATA_LOADED, handleDataLoaded)
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current)
                }
              }

              const handleDataLoaded = (e: Event) => {
                const tableId = (e as CustomEvent).detail.tableId

                // note: dont use early return here, because the table might not be loaded yet
                if (tableId === table.id) {
                  const newRow = table.GetRowDataByRowId(newRowId, language, userSpecific)
                  if (newRow) {
                    window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))
                    cleanup()
                    resolve(newRow)
                  }
                }
              }

              window.addEventListener(EVENT_KEYS.DB_TABLE_DATA_LOADED, handleDataLoaded)

              timeoutRef.current = setTimeout(() => {
                cleanup()
                resolve(null)
              }, WORKER_TIMEOUT_MS)
            })
            //
          } catch (err) {
            printAPIError(err as any)
            return null
          }
        } else {
          console.error('No target tableId reference for adding new row')
          return null
        }
      },
      [dbClient, language, userSpecific, formatRowDataPayload, printAPIError]
    )

    const handleUpdateRows = useCallback(
      async (handlerOption: ActionHandlerOption) => {
        if (!dbClient) return

        const { settings, sequenceId } = handlerOption

        const { dbTable, tableId, rowIds } = await parseMultipleRowIdsConfig(dbClient, handlerOption)

        if (dbTable && tableId && !!rowIds.length) {
          try {
            let updatedData = settings.data as Record<string, ActionInputdata>

            // get current rows
            const currentRows = dbTable.GetDbWorkerRows(language, userSpecific)

            // format the payload (rows to update)
            let payload = (
              await Promise.all(
                rowIds.map(async (rowId) => {
                  let rowIdx = dbTable.GetRowIndexByRowId(rowId, language, userSpecific)
                  let rowData = currentRows[rowIdx]
                  if (!rowData) return null
                  return {
                    ...rowData,
                    ...(await Object.entries(updatedData).reduce(async (prev, [col, inputData]) => {
                      let parsedValue = await parseActionSettingsData(inputData, sequenceId)
                      let hasValue = parsedValue !== null && parsedValue !== ''

                      if (parsedValue === '##clear_value##') {
                        const colType = dbTable?.columnsMap.get(col)?._Type
                        if (colType) {
                          parsedValue = getDefaultColumnValue(colType)
                        }
                      }

                      return {
                        ...(await prev),
                        ...(hasValue && { [col]: parsedValue }),
                      }
                    }, {})),
                  }
                })
              )
            ).filter(Boolean) as ITableRow[]

            // update dbTable data
            await dbTable.UpdateData(payload, language, userSpecific)

            window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))
            //
          } catch (err) {
            printAPIError(err as any)
          }
        } else {
          console.error('No tableId & rowId reference')
        }
      },
      [dbClient, parseMultipleRowIdsConfig, language, parseActionSettingsData, printAPIError, userSpecific]
    )

    const handleIncrementNumber = useCallback(
      async (handlerOption: ActionHandlerOption) => {
        if (!dbClient) return

        const { sequenceId } = handlerOption
        const actionSequence = actionSequenceRef.current.get(sequenceId)!
        const { triggerSource, external } = actionSequence

        let currentSettings = handlerOption.settings

        if (currentSettings.tableId === 'this-item') {
          currentSettings.tableId = triggerSource?.tableId ?? external?.detailsPage?.detailTableId
        }

        const { dbTable, tableId, rowIds } = await parseMultipleRowIdsConfig(dbClient, handlerOption)

        if (dbTable && tableId && !!rowIds.length) {
          try {
            // get current rows
            const currentRows = dbTable.GetDbWorkerRows(language, userSpecific)

            // format the payload (rows to update)
            let payload = (
              await Promise.all(
                rowIds.map(async (rowId) => {
                  let rowIdx = dbTable.GetRowIndexByRowId(rowId, language, userSpecific)
                  let rowData = currentRows[rowIdx]
                  if (!rowData) return null

                  const value = rowData[currentSettings.col]
                  const currentValue = (value ?? 0) as number
                  const incrementValue = Number(await parseActionSettingsData(currentSettings.data, sequenceId))
                  const nextValue = currentValue + incrementValue

                  if (typeof incrementValue !== 'number' || isNaN(incrementValue)) return null

                  return {
                    ...rowData,
                    [currentSettings.col]: nextValue,
                  }
                })
              )
            ).filter(Boolean) as ITableRow[]

            // update dbTable data
            await dbTable.UpdateData(payload, language, userSpecific)

            window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))
            //
          } catch (err) {
            printAPIError(err as any)
          }
        } else {
          console.error('No tableId & rowId reference')
        }
      },
      [dbClient, parseMultipleRowIdsConfig, language, parseActionSettingsData, printAPIError, userSpecific]
    )

    const handleToggleValue = useCallback(
      async (handlerOption: ActionHandlerOption) => {
        if (!dbClient) return

        const { sequenceId } = handlerOption
        const actionSequence = actionSequenceRef.current.get(sequenceId)!
        const { triggerSource, external } = actionSequence

        let currentSettings = handlerOption.settings
        if (currentSettings.tableId === 'this-item') {
          currentSettings.tableId = triggerSource?.tableId ?? external?.detailsPage?.detailTableId
        }

        const { dbTable, tableId, rowIds, isMultiple } = await parseMultipleRowIdsConfig(dbClient, handlerOption)

        if (dbTable && tableId && !!rowIds.length) {
          try {
            // get current rows
            const currentRows = dbTable.GetDbWorkerRows(language, userSpecific)

            // format the payload (rows to update)
            let payload = (
              await Promise.all(
                rowIds.map(async (rowId) => {
                  let rowIdx = dbTable.GetRowIndexByRowId(rowId, language, userSpecific)
                  let rowData = currentRows[rowIdx]
                  if (!rowData) return null
                  const value = rowData[currentSettings.col] as boolean
                  return {
                    ...rowData,
                    [currentSettings.col]: !value,
                  }
                })
              )
            ).filter(Boolean) as ITableRow[]

            // update dbTable data
            await dbTable.UpdateData(payload, language, userSpecific)

            window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))

            if (isMultiple) {
              handleResetCheckedList()
            }
            //
          } catch (err) {
            printAPIError(err as any)
          }
        } else {
          console.error('No tableId & rowId reference')
        }
      },
      [dbClient, parseMultipleRowIdsConfig, handleResetCheckedList, language, printAPIError, userSpecific]
    )

    const handleDeleteRows = useCallback(
      async (handlerOption: ActionHandlerOption) => {
        if (!dbClient) return

        const { dbTable, tableId, rowIds, isMultiple } = await parseMultipleRowIdsConfig(dbClient, handlerOption)

        if (dbTable && tableId && rowIds.length) {
          try {
            await dbTable.DeleteRows(rowIds, language)

            window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))

            if (activeLayerTableId && activeLayerDetailRowId && rowIds.includes(activeLayerDetailRowId)) {
              if (activeLayer.type === 'overlay') {
                // Close detail overlay for deleted item
                closeOverlay(activeLayer.id)
              } else {
                // Navigate back for deleted page item
                handleGoBack()
              }
            }

            if (isMultiple) {
              handleResetCheckedList()
            }
          } catch (err) {
            printAPIError(err as any)
          }
        } else {
          console.error('No tableId or rowIndex reference')
        }
      },
      [
        activeLayer.id,
        activeLayer.type,
        activeLayerTableId,
        activeLayerDetailRowId,
        closeOverlay,
        dbClient,
        parseMultipleRowIdsConfig,
        handleGoBack,
        handleResetCheckedList,
        language,
        printAPIError,
      ]
    )

    const handleDuplicateRows = useCallback(
      async (handlerOption: ActionHandlerOption) => {
        if (!dbClient) return

        const { dbTable, tableId, rowIds, isMultiple } = await parseMultipleRowIdsConfig(dbClient, handlerOption)

        if (dbTable && tableId && !!rowIds.length) {
          try {
            // duplicate rows
            await dbTable.DuplicateRows(rowIds, language, userSpecific)

            window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))

            if (isMultiple) {
              handleResetCheckedList()
            }
            //
          } catch (err) {
            printAPIError(err as any)
          }
        } else {
          console.error('No tableId & rowId reference')
        }
      },
      [dbClient, parseMultipleRowIdsConfig, handleResetCheckedList, language, printAPIError, userSpecific]
    )

    //--------------------------------------------------------------------------------------//
    //                                  Form Actions                                        //
    //--------------------------------------------------------------------------------------//

    const handleFormSubmitAdd = useCallback(
      async (payload: Record<string, any>, table: IDBTable) => {
        try {
          // add row request
          const newRowId = await table
            .AddRow(payload as ITableRow, language, userSpecific)
            .then((newRow) => newRow?._id)

          if (!newRowId) return null

          // wait for the table to be loaded, then grab the row data
          return new Promise<IRow | null>((resolve) => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
            }

            const cleanup = () => {
              window.removeEventListener(EVENT_KEYS.DB_TABLE_DATA_LOADED, handleDataLoaded)
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
              }
            }

            const handleDataLoaded = (e: Event) => {
              const tableId = (e as CustomEvent).detail.tableId

              // note: dont use early return here, because the table might not be loaded yet
              if (tableId === table.id) {
                const newRow = table.GetRowDataByRowId(newRowId, language, userSpecific)
                if (newRow) {
                  window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))
                  cleanup()
                  resolve(newRow)
                }
              }
            }

            window.addEventListener(EVENT_KEYS.DB_TABLE_DATA_LOADED, handleDataLoaded)

            timeoutRef.current = setTimeout(() => {
              cleanup()
              resolve(null)
            }, WORKER_TIMEOUT_MS)
          })
          //
        } catch (err) {
          printAPIError(err as any)
        }
      },
      [language, printAPIError, userSpecific]
    )

    const handleFormSubmitUpdate = useCallback(
      async (payload: Record<string, any>, dbTable: IDBTable, rowId: string) => {
        try {
          // get current rows
          const currentRows = dbTable.GetDbWorkerRows(language, userSpecific)

          // format the payload (rows to update)
          const updatedRows = (await Promise.all(
            [rowId].map(async (rowId) => {
              let rowIdx = dbTable.GetRowIndexByRowId(rowId, language, userSpecific)
              let rowData = currentRows[rowIdx] ?? {}
              return {
                ...rowData,
                ...payload,
              }
            })
          )) as ITableRow[]

          // update dbTable data
          await dbTable.UpdateData(updatedRows, language, userSpecific)

          // wait for the table to be loaded, then grab the row data
          return new Promise<void>((resolve) => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
            }

            const cleanup = () => {
              window.removeEventListener(EVENT_KEYS.DB_TABLE_DATA_LOADED, handleDataLoaded)
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
              }
            }

            const handleDataLoaded = (e: Event) => {
              const tableId = (e as CustomEvent).detail.tableId

              // note: dont use early return here, because the table might not be loaded yet
              if (tableId === dbTable.id) {
                window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))
                cleanup()
                resolve()
              }
            }

            window.addEventListener(EVENT_KEYS.DB_TABLE_DATA_LOADED, handleDataLoaded)

            timeoutRef.current = setTimeout(() => {
              cleanup()
              resolve()
            }, WORKER_TIMEOUT_MS)
          })
          //
        } catch (err) {
          printAPIError(err as any)
        }
      },
      [language, printAPIError, userSpecific]
    )

    const handleFormSubmit = useCallback(
      async (settings: IActionSettings, rowId?: string) => {
        if (!dbClient) return

        const isUpdate = !!rowId
        const { tableId, data } = settings

        if (!tableId || !data || !Object.keys(data).length) return

        const table = dbClient.tablesMap.get(tableId)
        if (!table) return

        try {
          // prepare payload
          const payload = await prepareFormPayload(data, tableId, dbClient, currentOrgId)

          if (isUpdate) {
            return handleFormSubmitUpdate(payload, table, rowId)
          } else {
            return handleFormSubmitAdd(payload, table)
          }
          //
        } catch (err) {
          printAPIError(err as any)
        }
      },
      [dbClient, currentOrgId, handleFormSubmitUpdate, handleFormSubmitAdd, printAPIError]
    )

    //--------------------------------------------------------------------------------------//
    //                                  Nav Actions                                         //
    //--------------------------------------------------------------------------------------//

    const handleOpenFormAddScreen = useCallback(
      async (settings: IActionSettings, tableId: string | undefined) => {
        if (!dbClient || !tableId) return exitActionQueue()

        const table = dbClient.tablesMap.get(tableId)
        if (!table) return exitActionQueue()

        const formDetailsPage = pages.find(
          (p) => p.tableId === tableId && p.nature === 'form' && p.title.startsWith('form-add')
        )
        if (!formDetailsPage) return exitActionQueue()

        // // Build an updated page copy for overlay so updated inputs are visible immediately
        // let overlayPageCopy: BuilderPage | null = null
        // try {
        //   const pageIdx = pages.findIndex((p) => p.id === formDetailsPage.id)
        //   if (pageIdx !== -1 && tables && tables.length) {
        //     const findFormEl = (els: BuilderDroppableElement[]): BuilderDroppableElement | undefined => {
        //       for (const el of els) {
        //         if (el.compId === 'form') return el
        //         const found = findFormEl(el.children)
        //         if (found) return found
        //       }
        //       return undefined
        //     }
        //     const formEl = findFormEl(formDetailsPage.elements as unknown as BuilderDroppableElement[])
        //     if (formEl) {
        //       const formId = formEl.id
        //       const currentSettings = { ...(formDetailsPage.elementSettings?.[formId] || {}) }
        //       if (!currentSettings?.[ComponentSettingKey.FormDataSource]) {
        //         currentSettings[ComponentSettingKey.FormDataSource] = tableId
        //       }
        //       const { children, childElSettings } = generateAutoFormFields(
        //         tables,
        //         formId,
        //         currentSettings,
        //         tableId,
        //         'form-add'
        //       )

        //       // clone and replace children for overlay copy
        //       const clonedElements: BuilderDroppableElement[] = JSON.parse(JSON.stringify(formDetailsPage.elements))
        //       const replaceLocal = (els: BuilderDroppableElement[]) => {
        //         for (const el of els) {
        //           if (el.compId === 'form' && el.id === formId) {
        //             el.children = children
        //             return true
        //           }
        //           if (replaceLocal(el.children)) return true
        //         }
        //         return false
        //       }
        //       replaceLocal(clonedElements)

        //       overlayPageCopy = {
        //         ...formDetailsPage,
        //         elements: clonedElements,
        //         elementSettings: {
        //           ...(formDetailsPage.elementSettings || {}),
        //           ...childElSettings,
        //           [formId]: currentSettings,
        //         },
        //       }

        //       // also update stored page state for page navigation case
        //       const prevChildIds = Array.isArray(formEl.children) ? formEl.children.map((c) => c.id) : []
        //       updatePageAlt(pageIdx, (prev) => {
        //         const replace = (els: BuilderDroppableElement[]) => {
        //           for (const el of els) {
        //             if (el.compId === 'form' && el.id === formId) {
        //               el.children = children
        //               return true
        //             }
        //             if (replace(el.children)) return true
        //           }
        //           return false
        //         }
        //         replace(prev.elements as unknown as BuilderDroppableElement[])
        //         if (prev.elementSettings && prevChildIds.length) {
        //           for (const cid of prevChildIds) delete prev.elementSettings[cid]
        //         }
        //         prev.elementSettings = {
        //           ...(prev.elementSettings || {}),
        //           ...childElSettings,
        //           [formId]: currentSettings,
        //         }
        //       })
        //     }
        //   }
        // } catch (err) {
        //   console.error('[FormAdd] refresh/build failed', err)
        // }

        const screenType = settings.screenType ?? 'page'

        switch (screenType) {
          case 'overlay':
            {
              const overlaySettings = { ...DEFAULT_OVERLAY_SETTINGS, ...(settings.overlaySettings || {}) }

              // show the page within overlay instead (prefer updated copy)
              showDetailScreenOverlay({
                // ...(overlayPageCopy || formDetailsPage),
                ...formDetailsPage,
                ...overlaySettings,
              })
              setPageAction({ flag: ActionFlag.Edit, page: formDetailsPage.id })
              setActiveTab(LeadingSidebarTab.Page)
            }
            break
          case 'page':
            {
              // show the detail page
              const tablePageId = table.settings._id
              const tableName = table.settings._Name + '-form-add'
              const tableSlug = createSlug(tableName.toLowerCase())

              goToPage({
                slug: tableSlug,
                state: {
                  tablePageId,
                },
              })
              setPageAction({ flag: ActionFlag.Edit, page: formDetailsPage.id })
              setActiveTab(LeadingSidebarTab.Page)
              await closeAllOverlays()
            }
            break
          default:
            break
        }
      },
      [dbClient, pages, showDetailScreenOverlay, setPageAction, setActiveTab, goToPage, closeAllOverlays]
    )

    const handleOpenDetailScreen = useCallback(
      async (
        settings: IActionSettings,
        rowId: string | undefined,
        detailPage: BuilderPage,
        type: 'detail' | 'form-update' | 'form-add'
      ) => {
        if (!dbClient) return exitActionQueue()

        const tableId = detailPage.tableId || ''

        // check if related table exist
        const dbTable = dbClient.tablesMap.get(tableId)
        if (!dbTable) return exitActionQueue()

        // Update title if not present
        // TEMP SOLUTION TO CHECK IF THE TITLE CONTAINS 'detail-page'
        if (window.appMode === 'preview') {
          if (!detailPage.title || detailPage.title.includes('detail-page')) {
            // Pick the first text column as title
            const textCol = dbTable.settings._Columns.find((col) => col._Type === ColumnTypes.Text)
            if (textCol) {
              const pageIdx = pages.findIndex((p) => p.id === detailPage.id)
              if (pageIdx !== -1) {
                updatePageAlt(pageIdx, (prev) => {
                  prev.title = textCol._id
                })
              }
            }
          }

          //* temporary fix: validate detail page bgColor (for existing apps)
          if (!isColorVariant(detailPage.bgColor)) {
            const pageBgColor = 'primary-95'

            // update page bgColor
            detailPage.bgColor = pageBgColor

            // update page bgColor in SDK
            const pageIdx = pages.findIndex((p) => p.id === detailPage.id)
            updatePageAlt(pageIdx, (prev) => {
              prev.bgColor = pageBgColor
            })
          }
        }

        switch (settings.screenType) {
          case 'overlay':
            {
              // show the page within overlay instead
              showDetailScreenOverlay({
                ...detailPage,
                ...settings.overlaySettings,
                rowIndex: -1, // we will load it later in the BuilderOverlayCore
                rowId,
              })
              setPageAction({ flag: ActionFlag.Edit, page: detailPage.id })
              setActiveTab(LeadingSidebarTab.Page)
            }
            break
          case 'page':
            {
              // show the detail page
              const tablePageId = dbTable.settings._id
              const tableName = {
                detail: dbTable.settings._Name,
                ['form-update']: dbTable.settings._Name + '-form',
                ['form-add']: dbTable.settings._Name + '-form-add',
              }[type]
              const tableSlug = createSlug(tableName.toLowerCase())

              goToPage({
                slug: tableSlug,
                rowId,
                state: {
                  tablePageId,
                  rowIndex: -1, // we will load it later in the BuilderPageCore
                },
              })
              setPageAction({ flag: ActionFlag.Edit, page: detailPage.id })
              setActiveTab(LeadingSidebarTab.Page)
              await closeAllOverlays()
            }
            break
        }

        handleResetCheckedList()
      },
      [
        closeAllOverlays,
        dbClient,
        goToPage,
        handleResetCheckedList,
        pages,
        setActiveTab,
        setPageAction,
        showDetailScreenOverlay,
        updatePageAlt,
      ]
    )

    //--------------------------------------------------------------------------------------//
    //                                PDF Generation                                        //
    //--------------------------------------------------------------------------------------//

    const handleGeneratePDF = useCallback(
      async (handlerOption: ActionHandlerOption) => {
        const { settings, sequenceId } = handlerOption

        const actionSequence = actionSequenceRef.current.get(sequenceId)!

        const { triggerSource, external, outputCache } = actionSequence

        if (!dbClient) return

        const tableId = triggerSource?.tableId ?? external?.detailsPage?.detailTableId
        const rowIndex = triggerSource?.rowIndex ?? external?.detailsPage?.detailRowIndex
        const rowId = triggerSource?.rowId ?? external?.detailsPage?.detailRowId

        try {
          // Parse the action settings to get filename, source template, and target column data
          const filenameValue =
            (await parseVariableTextActionOutput(
              settings.filename,
              dbClient,
              activeLayerClient,
              userEmail,
              language,
              userSpecific,
              tableId,
              rowId,
              external?.allActions || [],
              outputCache,
              external
            )) ?? `document`

          const sourceTemplateValue = await parseActionSettingsData(settings.sourceColumn, sequenceId)
          const targetColumn = settings.targetColumn

          if (!filenameValue || !sourceTemplateValue) {
            console.error('Missing required data for PDF generation:', {
              filename: filenameValue,
              template: !!sourceTemplateValue,
              targetColumn: targetColumn,
            })
            return
          }

          // Get current row information for updating the target column
          const currentTableId = targetColumn.tbl ?? tableId

          if (!currentTableId) {
            console.error('Missing table reference for PDF generation')
            return
          }

          const dbTable = dbClient.tablesMap.get(currentTableId)
          if (!dbTable) {
            console.error('Target table not found:', currentTableId)
            return
          }

          const currentRowIndex = targetColumn?.row
            ? parseInt(targetColumn.row) - 1
            : external?.detailsPage
              ? external?.detailsPage?.detailRowIndex
              : rowIndex

          if (currentRowIndex === undefined || currentRowIndex === null || currentRowIndex < 0) {
            console.error('Missing row reference for PDF generation')
            return
          }

          let pdfUrl = ''

          // Generate PDF from template content
          try {
            // dynamic import -> optimize bundle size
            const { default: generateAndUploadPDF } = await import('@/shared/pdf-generator')
            pdfUrl = await generateAndUploadPDF(
              sourceTemplateValue,
              filenameValue,
              currentOrgId,
              settings.paperSize,
              settings.useHtmlFormat
            )
          } catch (err) {
            console.error('Error in PDF generation process:', err)
            return Promise.reject(err)
          }

          // Update the target column with the PDF URL
          const currentRows = dbTable.GetDbWorkerRows(language, userSpecific)
          const rowData = currentRows[currentRowIndex]

          if (!rowData) {
            console.error('Row not found for updating PDF URL:', targetColumn.rowId)
            return
          }

          const columnId = targetColumn.value || targetColumn.col || targetColumn

          const updatedRowData = {
            ...rowData,
            [columnId]: pdfUrl,
          }

          await dbTable.UpdateData([updatedRowData], language, userSpecific)

          window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_ROWS_UPDATED))
          //
        } catch (error) {
          console.error('Error in PDF generation process:', error)
          printAPIError(error as any)
        }
      },
      [
        dbClient,
        activeLayerClient,
        userEmail,
        language,
        userSpecific,
        parseActionSettingsData,
        currentOrgId,
        printAPIError,
      ]
    )

    //--------------------------------------------------------------------------------------//
    //                                  Action Handlers                                     //
    //--------------------------------------------------------------------------------------//

    /**
     * action handlers define how each of action type will be performed
     */
    const actionHandlersMap: ActionHandlers = useMemo(
      () => ({
        // alerts
        [IActionType.ShowDialog]: ({ sequenceId, settings }) => {
          return new Promise<void>(() => {
            const dialog = dialogs.find((d) => d.id === settings.dialog)
            if (!dialog) return exitActionQueue()

            // store dialog id to sequence id
            dialogSequenceRef.current.set(dialog.id, sequenceId)

            handleCloseLastSnackbar()

            // clean up loading state before hanging the promise
            const actionSequence = actionSequenceRef.current.get(sequenceId)
            if (actionSequence?.triggerSource?.id) {
              setActionLoadingMap((prev) => {
                const newMap = new Map(prev)
                newMap.delete(actionSequence.triggerSource?.id as string)
                return newMap
              })
            }
            setDialogAction({ dialog, flag: ActionFlag.Preview, dialogSettings: settings.dialogSettings } as any)
            // NOTE: do not resolve the promise here,
            // the promise should be stale and disturb the whole action promise chains,
            // so that the next action or close alert action can then resume the sequence.
          })
        },
        [IActionType.CloseAlert]: () => {
          if (!dialogAction) return

          const activeDialog = dialogs.find(({ id }) => id === dialogAction?.dialog.id)

          if (!!activeDialog) {
            setDialogAction({
              ...dialogAction,
              flag: removeActionFlag(dialogAction.flag, ActionFlag.Preview),
            })
          }
        },
        [IActionType.ShowSnackbar]: async ({ sequenceId, settings }) => {
          return new Promise((resolve) => {
            const actionSequence = actionSequenceRef.current.get(sequenceId)
            if (!actionSequence) return exitActionQueue()

            const { triggerSource, external, outputCache } = actionSequence

            parseVariableTextActionOutput(
              settings.value,
              dbClient,
              activeLayerClient,
              userEmail,
              language,
              userSpecific,
              triggerSource?.tableId ?? external?.detailsPage?.detailTableId,
              triggerSource?.rowId ?? external?.detailsPage?.detailRowId,
              external?.allActions || [],
              outputCache,
              external
            )
              .then(async (valueInText) => {
                const getHexaColor = (color: string | undefined) =>
                  isColorVariant(color)
                    ? getComputedStyle(document.documentElement).getPropertyValue(`--color-${color}`)
                    : color

                let titleInText = ''
                if (settings.title) {
                  try {
                    titleInText = await parseVariableTextActionOutput(
                      settings.title,
                      dbClient,
                      activeLayerClient,
                      userEmail,
                      language,
                      userSpecific,
                      triggerSource?.tableId ?? external?.detailsPage?.detailTableId,
                      triggerSource?.rowId ?? external?.detailsPage?.detailRowId,
                      external?.allActions || [],
                      outputCache,
                      external
                    )
                  } catch (error) {
                    console.warn('Failed to parse title:', error)
                    titleInText = ''
                  }
                }

                const iconColorHex = getHexaColor(settings.iconColor)

                sonner.show({
                  title: titleInText,
                  description: valueInText,
                  bgColor: settings.bgColor,
                  icon: <MaterialIcon icon={settings.icon} color={iconColorHex} size={20} />,
                })
              })
              .finally(() => {
                resolve()
              })
          })
        },
        [IActionType.PerformNextAction]: () => {
          return new Promise((resolve) => {
            // close alert
            const activeDialog = dialogs.find(({ id }) => id === dialogAction?.dialog.id)
            if (!!activeDialog && dialogAction) {
              setDialogAction({
                ...dialogAction,
                flag: removeActionFlag(dialogAction.flag, ActionFlag.Preview),
              })
            }

            // perform next action queue
            resolve()
          })
        },

        // communication
        [IActionType.ComposeEmail]: async ({ sequenceId, settings }) => {
          const actionSequence = actionSequenceRef.current.get(sequenceId)
          if (!actionSequence) return exitActionQueue()

          const email = await parseActionSettingsData(settings.email, sequenceId)
          const subject = await parseActionSettingsData(settings.subject, sequenceId)
          const cc = await parseActionSettingsData(settings.cc, sequenceId)
          const bcc = await parseActionSettingsData(settings.bcc, sequenceId)
          const message = await parseActionSettingsData(settings.message, sequenceId)

          if (!email) {
            return new Promise((resolve) => resolve())
          }

          let openEmailUrl = `mailto:${email}`
          const params = []

          if (cc) params.push(`cc=${encodeURIComponent(cc)}`)
          if (bcc) params.push(`bcc=${encodeURIComponent(bcc)}`)
          if (subject) params.push(`subject=${encodeURIComponent(subject)}`)
          if (message) params.push(`body=${encodeURIComponent(message)}`)

          if (params.length > 0) {
            openEmailUrl += '?' + params.join('&')
          }

          window.open(openEmailUrl, '_blank')
          return new Promise((resolve) => setTimeout(() => resolve(), 500))
        },
        [IActionType.MakePhoneCall]: async ({ settings, sequenceId }) => {
          const phone = await parseActionSettingsData(settings, sequenceId)

          if (phone) {
            window.location.href = `tel:${phone}`
          }
          return new Promise((resolve) => setTimeout(() => resolve(), 500))
        },
        [IActionType.StartSMS]: async ({ settings, sequenceId }) => {
          const phone = await parseActionSettingsData(settings.phone, sequenceId)
          const message = await parseActionSettingsData(settings.message, sequenceId)

          let userAgent = navigator.userAgent || navigator.vendor

          if (userAgent.match(/Android/i)) {
            window.open(`sms://${phone}/?body=${encodeURIComponent(message)}`)
          } else if (userAgent.match(/iPhone/i)) {
            window.open(`sms://${phone}/&body==${encodeURIComponent(message)}`)
          } else {
            window.open(`sms://${phone}?&body=${encodeURIComponent(message)}`)
          }
          return new Promise((resolve) => setTimeout(() => resolve(), 500))
        },
        [IActionType.StartWhatsApp]: async ({ settings, sequenceId }) => {
          const phone = await parseActionSettingsData(settings.phone, sequenceId)
          const message = await parseActionSettingsData(settings.message, sequenceId)

          // Format phone number to remove spaces and ensure proper format
          let phoneNumber = formatPhoneNumber(phone)

          if (!!phoneNumber) {
            //TODO: Discuss this and refactor
            // const isBusiness = !!settings.business
            const isBusiness = false
            const baseUrl = isBusiness ? 'https://api.whatsapp.com/send' : 'https://api.whatsapp.com/send'

            let formattedMessage = ''

            if (message) {
              const msgWithBuster = addCacheBusterToFirstUrl(message)
              formattedMessage = sanitizeWhatsappMessage(msgWithBuster)
            }

            const finalUrl = isBusiness
              ? `${baseUrl}/${phoneNumber}${formattedMessage ? `?text=${formattedMessage}` : ''}`
              : `${baseUrl}?phone=${phoneNumber}${formattedMessage ? `&text=${formattedMessage}` : ''}`
            window.open(finalUrl, '_blank')

            // const isPWA =
            //   window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true

            // if (isPWA) {
            //   window.location.href = finalUrl
            // } else {
            //   window.open(finalUrl, '_blank', 'noopener,noreferrer')
            // }
          }
          return new Promise((resolve) => setTimeout(() => resolve(), 500))
        },

        // misc
        [IActionType.Clipboard]: async ({ settings, sequenceId }) => {
          const valueToCopy = await parseActionSettingsData(settings, sequenceId)
          return new Promise<void>((resolve) => {
            if (valueToCopy) {
              copyToClipboard(valueToCopy)
              sonner.show({ description: `Copied: ${valueToCopy}`, duration: 4000 })
            }
            setTimeout(() => {
              resolve()
            }, 500)
          })
        },
        [IActionType.Download]: async ({ settings, sequenceId }) => {
          const fileUrl = await parseActionSettingsData(settings, sequenceId)

          if (fileUrl) {
            return downloadFile(fileUrl)
          }
        },
        [IActionType.PlaySound]: async ({ settings, sequenceId }) => {
          const fileUrl = await parseActionSettingsData(settings, sequenceId)

          if (fileUrl) {
            return playAudio(fileUrl)
          }
        },
        [IActionType.Wait]: async ({ settings, sequenceId }) => {
          try {
            const durationValue = await parseActionSettingsData(settings, sequenceId)
            const seconds = typeof durationValue === 'number' ? durationValue : parseInt(durationValue as string, 10)
            const duration = isNaN(seconds) || seconds <= 0 ? 1 : seconds
            return new Promise((resolve) => setTimeout(resolve, duration * 1000))
          } catch (error) {
            return new Promise((resolve) => setTimeout(resolve, 0))
          }
        },
        // nav
        [IActionType.OpenOverlay]: ({ settings }) => {
          const overlayId = settings.overlay as string
          const overlayToShow = overlays.find((o) => o.id === overlayId) ?? overlays[0]
          if (overlayToShow) {
            showOverlay(overlayToShow.id)
          }
          return new Promise((resolve) => setTimeout(resolve, 300)) // wait for overlay animation to finish
        },
        [IActionType.CloseOverlay]: async () => {
          const lastOverlay = activeOverlays[activeOverlays.length - 1]
          if (lastOverlay) {
            closeOverlay(lastOverlay.id)
          }
          return new Promise((resolve) => setTimeout(resolve, 0)) // no need to wait for overlay animation to finish
        },
        [IActionType.GoToScreen]: async ({ settings }) => {
          const nextPageId = settings.screen as string

          const hasOpenedOverlays = !!activeOverlays.length

          if (hasOpenedOverlays) {
            await closeAllOverlays()
          }

          if (nextPageId) {
            const page = pages.find((p) => p.id === nextPageId)
            if (page && page.slug) {
              goToPageSafely(page.slug)
              setActiveComponent(undefined)
            }
          }
        },
        [IActionType.OpenMenu]: ({ settings, sequenceId }) => {
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { elRef } = actionSequence

          return new Promise((resolve) => {
            if (!elRef?.current) return

            const actMenu = menus.find((m) => m.id === settings.menu)

            if (!actMenu) return

            const sameMenu = actMenu.id === activeMenu?.id
            const sameAnchor = activeMenuAnchorRef.current?.id === elRef.current?.id

            startTransition(() => {
              if (!sameAnchor) {
                activeMenuAnchorRef.current = elRef.current
              }
              if (!sameMenu) {
                setActiveMenu(actMenu)
              }

              if (menuOpened) {
                setMenuOpened(false)
              } else {
                setMenuOpened(true)
              }
            })

            // TODO -> somehow we can't delay this action since it will make the opened menu closing by itself
            setTimeout(() => {
              resolve()
            }, 1000)
          })
        },
        [IActionType.NextItem]: async () => {
          if (!dbClient) return
          if (!activeLayerTableId || !activeLayerDetailRowId) return

          handleCloseLastSnackbar()

          let pageId = activeLayer.id

          let tableId = activeLayerTableId
          const dbTable = dbClient.tablesMap.get(tableId)
          if (!dbTable) return

          const activeLayerDetailRowIndex = dbTable.GetRowIndexByRowId(activeLayerDetailRowId, language, userSpecific)
          if (activeLayerDetailRowIndex === -1) return

          const rows = dbTable.GetDbWorkerRows(language, userSpecific)

          let nextRowIndex = activeLayerDetailRowIndex + 1
          let notLastItem = nextRowIndex < rows.length
          let nextRowId = notLastItem ? rows[nextRowIndex]._id : undefined

          if (notLastItem && nextRowId) {
            if (activeLayer.type === 'overlay') {
              // go to next detail overlay
              changeDetailsOverlayIndex((prev) => {
                prev.rowIndex = nextRowIndex
                prev.rowId = nextRowId
              })
            } else {
              // go to next detail page
              const tablePageId = dbTable.settings._id
              const tableName = dbTable.settings._Name
              const tableSlug = createSlug(tableName.toLowerCase())
              goToPage({
                slug: tableSlug,
                rowId: nextRowId,
                state: { tablePageId, rowIndex: nextRowIndex },
                replace: true,
              })
            }
            setPageAction({ flag: ActionFlag.Edit, page: pageId })
            return Promise.resolve()
          } else {
            sonner.show({ description: t('You are already on the last item'), duration: 4000 })
            setTimeout(() => {
              return Promise.resolve()
            }, 4000)
          }
        },
        [IActionType.PrevItem]: async () => {
          if (!dbClient) return
          if (!activeLayerTableId || !activeLayerDetailRowId) return

          handleCloseLastSnackbar()

          let pageId = activeLayer.id

          let tableId = activeLayerTableId
          const dbTable = dbClient.tablesMap.get(tableId)
          if (!dbTable) return

          const activeLayerDetailRowIndex = dbTable.GetRowIndexByRowId(activeLayerDetailRowId, language, userSpecific)
          if (activeLayerDetailRowIndex === -1) return

          const rows = dbTable.GetDbWorkerRows(language, userSpecific)
          let prevRowIndex = activeLayerDetailRowIndex - 1
          let notFirstItem = prevRowIndex >= 0
          let prevRowId = notFirstItem ? rows[prevRowIndex]._id : undefined
          if (notFirstItem && prevRowId) {
            if (activeLayer.type === 'overlay') {
              // go to next detail overlay
              changeDetailsOverlayIndex((prev) => {
                prev.rowIndex = prevRowIndex
                prev.rowId = prevRowId
              })
            } else {
              // go to next detail page
              const tablePageId = dbTable.settings._id
              const tableName = dbTable.settings._Name
              const tableSlug = createSlug(tableName.toLowerCase())
              goToPage({
                slug: tableSlug,
                rowId: prevRowId,
                state: { tablePageId, rowIndex: prevRowIndex },
                replace: true,
              })
            }
            setPageAction({ flag: ActionFlag.Edit, page: pageId })
            return Promise.resolve()
          } else {
            sonner.show({ description: t('You are already on the first item'), duration: 4000 })
            setTimeout(() => {
              return Promise.resolve()
            }, 4000)
          }
        },
        [IActionType.ShowDetailsScreen]: async ({ settings, sequenceId }) => {
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { triggerSource, external, redirectCache } = actionSequence

          const tableId = triggerSource?.tableId ?? external?.detailsPage?.detailTableId
          const rowId = triggerSource?.rowId ?? external?.detailsPage?.detailRowId

          // todo: check if rowId is in action output cache
          const redirectData = redirectCache.get('action-workflows-redirect')
          if (redirectData) {
            const rowId = redirectData.rowId
            const tableId = redirectData.tableId
            const detailsPage = pages.find((p) => !p?.nature && p.tableId === tableId)

            if (detailsPage) {
              // clear cache
              redirectCache.delete('action-workflows-redirect')
              return handleOpenDetailScreen(settings, rowId, detailsPage, 'detail')
            }
          }

          const currentRowId = rowId || triggerSource?.rowId
          if (!tableId || !currentRowId) return exitActionQueue()

          let detailTableId = tableId
          let detailRowId = currentRowId

          const { userId } = external?.comment || {}
          if (!!userId && appUserTable) {
            detailTableId = appUserTable?.id
            detailRowId = userId
          }

          const detailsPage = pages.find((p) => !p?.nature && p.tableId === detailTableId)
          if (!detailsPage) return

          // Open details page
          return handleOpenDetailScreen(settings, detailRowId, detailsPage, 'detail')
        },
        [IActionType.GoBack]: () => {
          handleGoBack()
        },
        [IActionType.Logout]: () => {
          handleLogout()
        },

        // open
        [IActionType.OpenLink]: async ({ settings, sequenceId }) => {
          const url = await parseActionSettingsData(settings.value ?? settings, sequenceId)

          if (url) {
            window.open(url, '_blank')
          }
          return new Promise((resolve) => setTimeout(() => resolve(), 500))
        },
        [IActionType.OpenFile]: async ({ settings, sequenceId }) => {
          const url = await parseActionSettingsData(settings, sequenceId)

          if (url) {
            window.open(url, '_blank')
          }
          return new Promise((resolve) => setTimeout(() => resolve(), 500))
        },
        [IActionType.OpenMap]: async ({ settings, sequenceId }) => {
          let address = await parseActionSettingsData(settings, sequenceId)

          if (typeof address === 'object' && 'Lat' in address && 'Lon' in address) {
            let { Lat, Lon } = address
            address = `${Lat},${Lon}`
          }

          if (address) {
            return openGoogleMaps(address)
          }
        },

        // data
        [IActionType.AddNewRow]: (options: ActionHandlerOption) => {
          const { actionId, settings, sequenceId } = options
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { outputCache, redirectCache } = actionSequence

          return new Promise<void>((resolve) => {
            handleAddRow(options)
              .then((newRow) => {
                if (actionId) {
                  outputCache.set(actionId, newRow)
                }
                if (newRow && newRow._id && settings.tableId) {
                  redirectCache.set('action-workflows-redirect', {
                    rowId: newRow._id,
                    tableId: settings.tableId,
                  })
                }
              })
              .finally(resolve)
          })
        },
        [IActionType.UpdateRow]: (options: ActionHandlerOption) => {
          return new Promise<void>((resolve) => {
            handleUpdateRows(options).finally(resolve)
          })
        },

        [IActionType.IncrementValue]: (options) => {
          return new Promise<void>((resolve) => {
            handleIncrementNumber(options).finally(resolve)
          })
        },

        [IActionType.ToggleValue]: (options) => {
          return new Promise<void>((resolve) => {
            handleToggleValue(options).finally(resolve)
          })
        },
        [IActionType.DeleteRow]: (options: ActionHandlerOption) => {
          return new Promise<void>((resolve) => {
            handleDeleteRows(options).finally(resolve)
          })
        },

        [IActionType.DuplicateRow]: (options: ActionHandlerOption) => {
          return new Promise<void>((resolve) => {
            handleDuplicateRows(options).finally(resolve)
          })
        },

        [IActionType.GeneratePDF]: (options: ActionHandlerOption) => {
          return new Promise<void>((resolve, reject) => {
            handleGeneratePDF(options).catch(reject).finally(resolve)
          })
        },
        [IActionType.SetGlobalVariable]: async (options: ActionHandlerOption) => {
          const { settings, sequenceId } = options
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { outputCache, redirectCache, triggerSource, external } = actionSequence

          // resolve name and value
          const varName = typeof settings.name === 'object' ? settings.name.value : settings.name

          const varValue = await parseVariableTextActionOutput(
            settings.value,
            dbClient,
            activeLayerClient,
            userEmail,
            language,
            userSpecific,
            triggerSource?.tableId ?? external?.detailsPage?.detailTableId,
            triggerSource?.rowId ?? external?.detailsPage?.detailRowId,
            external?.allActions || [],
            outputCache,
            external
          )

          // update global variable client
          window.prettyLog('[Global Variables] update', ['set', varName, varValue], '#9e620d')
          globalVariableClient?.setValue(varName, varValue)

          return new Promise((resolve) => setTimeout(() => resolve(), 50))
        },
        [IActionType.ClearGlobalVariable]: async ({ settings }) => {
          // resolve name
          const varName = typeof settings.value === 'object' ? settings.value.value : settings.value

          // update global variable client
          window.prettyLog('[Global Variables] update', ['clear', varName], '#9e620d')
          globalVariableClient?.clearValue(varName)

          return new Promise((resolve) => setTimeout(() => resolve(), 50))
        },

        // form
        [IActionType.FormAddRow]: (options: ActionHandlerOption) => {
          const { actionId, settings, sequenceId } = options
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { outputCache, redirectCache } = actionSequence

          return new Promise<void>((resolve) => {
            handleFormSubmit(settings)
              .then((newRow) => {
                if (actionId) {
                  outputCache.set(actionId, newRow)
                }

                if (newRow && newRow._id && settings.tableId) {
                  redirectCache.set('action-workflows-redirect', {
                    rowId: newRow._id,
                    tableId: settings.tableId,
                  })
                }

                // todo: convert into action instead
                // window.dispatchEvent(new CustomEvent(EVENT_KEYS.BUILDER_SCROLL_TOP))
              })
              .finally(resolve)
          })
        },
        [IActionType.FormUpdateRow]: (options: ActionHandlerOption) => {
          const { settings, sequenceId } = options
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { triggerSource, external } = actionSequence

          const rowId = triggerSource?.rowId ?? external?.detailsPage?.detailRowId

          return new Promise<void>((resolve) => {
            handleFormSubmit(settings, rowId)
              .then(() => {
                // todo: convert into action instead
                // window.dispatchEvent(new CustomEvent(EVENT_KEYS.BUILDER_SCROLL_TOP))
              })
              .finally(resolve)
          })
        },
        [IActionType.FormUpdateItemScreen]: async (options: ActionHandlerOption) => {
          const { settings, sequenceId } = options
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { triggerSource, external } = actionSequence

          const tableId = triggerSource?.tableId ?? external?.detailsPage?.detailTableId
          const rowId = triggerSource?.rowId ?? external?.detailsPage?.detailRowId

          if (!appUserTable || !project) return exitActionQueue()

          const { formType } = settings

          const currentRowId = formType === 'user' ? userId : rowId
          const targetTableId = formType === 'user' ? appUserTable.id : tableId
          if (!targetTableId || !currentRowId || !pages.length) return exitActionQueue()

          // Get form details page
          const formDetailsPage = pages.find(
            (p) => p.tableId === targetTableId && p.nature === 'form' && p.title.startsWith('form-detail')
          )

          if (!formDetailsPage) return

          // redirect user to login page if not logged in
          if (formType === 'user' && !userId) {
            goToPage({ slug: unauthenticatedPageSlug, redirect: formDetailsPage.slug })
            return exitActionQueue()
          }

          // Open form details page
          return handleOpenDetailScreen(settings, currentRowId, formDetailsPage, 'form-update')
        },

        // TODO
        [IActionType.FormAddItemScreen]: async (options: ActionHandlerOption) => {
          const { settings, sequenceId } = options
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { triggerSource, external } = actionSequence

          const tableId = triggerSource?.tableId ?? external?.detailsPage?.detailTableId

          if (!appUserTable || !project) return exitActionQueue()

          return handleOpenFormAddScreen(settings, tableId)
        },

        //
        [IActionType.TemporaryVariable]: async (options: ActionHandlerOption) => {
          const { settings, actionId, sequenceId } = options
          const actionSequence = actionSequenceRef.current.get(sequenceId)!
          const { outputCache, external } = actionSequence

          const nameInText = parseVariableText(
            settings.name,
            dbClient,
            activeLayerClient,
            userEmail,
            language,
            userSpecific,
            undefined,
            undefined,
            outputCache,
            external
          )
          const valueInText = parseVariableText(
            settings.value,
            dbClient,
            activeLayerClient,
            userEmail,
            language,
            userSpecific,
            undefined,
            undefined,
            outputCache,
            external
          )

          const variables = JSON.parse(localStorage.getItem(STORAGE_KEYS.TEMPORARY_VARIABLES) || '{}')
          const combinedName = `${actionId}-${nameInText}`

          // Check if value is a date to override the value with the current date
          // let isDate = false
          // if (value instanceof Date && !isNaN(value.getTime())) {
          //   isDate = true
          // } else if (typeof value === 'string') {
          //   // Try to parse string as date
          //   const parsed = new Date(value)
          //   if (!isNaN(parsed.getTime())) {
          //     isDate = true
          //   }
          // }

          if (nameInText && valueInText) {
            localStorage.setItem(
              STORAGE_KEYS.TEMPORARY_VARIABLES,
              JSON.stringify({ ...variables, [combinedName]: valueInText })
            )
          }

          return new Promise((resolve) => setTimeout(() => resolve(), 50))
        },
      }),
      [
        dialogs,
        handleCloseLastSnackbar,
        setDialogAction,
        dialogAction,
        dbClient,
        activeLayerClient,
        userEmail,
        language,
        userSpecific,
        parseActionSettingsData,
        overlays,
        showOverlay,
        activeOverlays,
        closeOverlay,
        closeAllOverlays,
        pages,
        goToPageSafely,
        setActiveComponent,
        menus,
        activeMenu?.id,
        menuOpened,
        activeLayerTableId,
        activeLayerDetailRowId,
        activeLayer.id,
        activeLayer.type,
        setPageAction,
        changeDetailsOverlayIndex,
        goToPage,
        appUserTable,
        handleOpenDetailScreen,
        handleGoBack,
        handleLogout,
        handleAddRow,
        handleUpdateRows,
        handleIncrementNumber,
        handleToggleValue,
        handleDeleteRows,
        handleDuplicateRows,
        handleGeneratePDF,
        handleFormSubmit,
        project,
        userId,
        unauthenticatedPageSlug,
        handleOpenFormAddScreen,
      ]
    )

    //--------------------------------------------------------------------------------------//
    //                                Simulate Action                                       //
    //--------------------------------------------------------------------------------------//

    /**
     * Initialize action sequence
     *
     */
    const handleInit = useCallback(
      (sequenceId: string, opts: SimulateActionOptions, type: ActionSeqType = 'normal') => {
        const { actions, rowIndex, tableId, rowId, elRef, multipleOptions, external } = opts

        // initialize sequence
        let sequence = actionSequenceRef.current.get(sequenceId)

        if (!sequence) {
          sequence = {
            sequenceId,
            actions,
            queue: [],
            outputCache: new Map(),
            redirectCache: new Map(),
            triggerSource: {
              id: opts.triggerSource?.id,
              label: opts.triggerSource?.label,
              type: opts.triggerSource?.type,
              tableId,
              rowIndex,
              rowId,
            },
            external,
            elRef,
            multipleOptions,
          }
        }

        // initialize details page info to external
        if ((!sequence.external || !sequence.external.detailsPage) && activeLayerTableId) {
          sequence.external = {
            ...external,
            detailsPage: {
              detailTableId: activeLayerTableId,
              detailRowId: activeLayerDetailRowId,
            },
          }
        }

        // ?for logging purpose only!
        if (type === 'queue') {
          sequence.actions = [...actions]
          sequence.queue = []
        }

        actionSequenceRef.current.set(sequenceId, { ...sequence })

        return sequence
        //
      },
      [activeLayerDetailRowId, activeLayerTableId]
    )

    /**
     * Cleanup action sequence
     */
    const handleCleanup = useCallback((sequenceId: string) => {
      actionSequenceRef.current.delete(sequenceId)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }, [])

    // access advanced anction executor
    const { executeAdvancedActions, executePluginAction } = useSimulateActionV2(
      actionHandlersMap,
      currentOrgId,
      parseActionSettingsData,
      actionSequenceRef
    )

    // action loading state
    const [isLoading, setIsLoading] = useState(false)
    const [actionLoadingMap, setActionLoadingMap] = useState<Map<string, boolean>>(new Map())

    /**
     * Main function to execute the actions
     */
    const executeActionsV2 = useCallback(
      async (sequenceId: string, opts: SimulateActionOptions, type: ActionSeqType = 'normal') => {
        const { actions, external } = opts

        if (!actions || !actions.length) return

        setIsLoading(true)
        window.dispatchEvent(new CustomEvent(EVENT_KEYS.GLOBAL_APP_LOADING, { detail: { loading: true } }))
        if (opts.triggerSource?.id) {
          setActionLoadingMap((prev) => {
            const newMap = new Map(prev)
            newMap.set(opts.triggerSource?.id as string, true)
            return newMap
          })
        }

        // initialize sequence
        const actionSequence = handleInit(sequenceId, opts, type)

        if (!opts.dialogId) {
          console.log(`%cACTION START`, 'background: #a57b1a;color:white;padding:2px', actionSequence)
        }

        let shouldCleanup = true

        try {
          // only check the first action for extended actions
          const hasAdvancedActions = actions[0].nextAction !== undefined

          if (hasAdvancedActions) {
            // Find the first action to start execution
            const firstAction = actions.find((action) => !actions.some((other) => other.nextAction === action.id))
            const startId = firstAction?.id || actions[0]?.id

            // set allActions in external
            actionSequenceRef.current.get(sequenceId)!.external = {
              ...external,
              allActions: actions,
            }

            if (startId) {
              await executeAdvancedActions(sequenceId, actions, startId)
            }
          } else {
            await actions.reduce(async (prevActions, action, currentIdx) => {
              // execute previous actions
              await prevActions

              const { settings, type: actionType } = action

              // handle plugin
              if (action.settings.pluginAction && currentOrgId) {
                await executePluginAction(action, sequenceId)
              }

              // get action handler
              const actionHandler = actionHandlersMap[actionType]
              if (!actionHandler) return Promise.resolve()

              // store remaining actions in queue
              if (actionType === IActionType.ShowDialog) {
                let nextQueue = actions.filter((a, idx) => idx > currentIdx)
                if (!!nextQueue.length) {
                  actionSequenceRef.current.get(sequenceId)!.queue = [...nextQueue]
                  shouldCleanup = false
                }
              }

              // continue queue execution
              if (actionType === IActionType.PerformNextAction) {
                console.info(`%cPERFORM_NEXT_ACTION`, 'background: #a57b1a;color:white;padding:2px', sequenceId)

                // run perform next action handler to close dialog
                await actionHandler({ sequenceId, settings, actionId: action.id })

                // get action sequence from dialog id
                const actionSequence = actionSequenceRef.current.get(sequenceId)
                if (!actionSequence) return exitActionQueue()

                // execute the queued actions recursively
                await executeActionsV2(
                  sequenceId,
                  {
                    actions: actionSequence.queue,
                    tableId: actionSequence.triggerSource?.tableId,
                    rowIndex: actionSequence.triggerSource?.rowIndex,
                    rowId: actionSequence.triggerSource?.rowId,
                    elRef: actionSequence.elRef,
                    multipleOptions: actionSequence.multipleOptions,
                    external: actionSequence.external,
                  },
                  'queue'
                )

                shouldCleanup = false

                // exit early since we're done executing queued actions
                return
              }

              // execute action handler
              await actionHandler({
                sequenceId,
                actionId: action.id,
                settings,
              })

              //
            }, Promise.resolve())
          }

          //
        } catch (error) {
          console.error('❌ Error simulating action', error)
          if (window.appMode === 'preview') {
            sonner.show({ description: 'Ooops...something went wrong!', bgColor: 'neutral-0' })
          }
          //
        } finally {
          setIsLoading(false)
          window.dispatchEvent(new CustomEvent(EVENT_KEYS.GLOBAL_APP_LOADING, { detail: { loading: false } }))

          if (opts.triggerSource?.id) {
            setActionLoadingMap((prev) => {
              const newMap = new Map(prev)
              newMap.delete(opts.triggerSource?.id as string)
              return newMap
            })
          }

          if (!opts.dialogId) {
            console.log(`%cACTION DONE`, 'background: #338d6b;color:white;padding:2px', sequenceId)
          }

          // cleanup
          if (shouldCleanup) {
            handleCleanup(sequenceId)
          }
        }
      },
      [handleInit, executeAdvancedActions, actionHandlersMap, currentOrgId, executePluginAction, handleCleanup]
    )

    /**
     * Backward compatible simulateAction
     */
    const simulateAction = useCallback(
      async (opts: SimulateActionOptions) => {
        if (!dbClient || !dbWorker) return

        // collect table ids from actions
        const tableIdsMap = collectTableIds(opts.actions, dbClient)

        // load table data
        for (const tableId of tableIdsMap.keys()) {
          if (loadedTables.has(tableId)) continue // skip if already loaded
          dbWorker.sendAction({
            type: 'get_table_data',
            payload: { table_id: tableId, language },
            meta: { from: 'simulateAction' },
          })
        }

        let sequenceId = uuidv4()

        // if dialogId is provided, we will continue the action sequence after the dialog is closed
        if (opts.dialogId) {
          const prevSequenceId = dialogSequenceRef.current.get(opts.dialogId)
          if (prevSequenceId) {
            sequenceId = prevSequenceId
          }
        }

        return executeActionsV2(sequenceId, opts, 'normal')
      },
      [dbClient, dbWorker, executeActionsV2, language, loadedTables]
    )

    return { simulateAction, isLoading, actionLoadingMap }
  }
)

export { BuilderActionProviderV2, useBuilderActionContextV2 }
